import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import BrandDashboardLayout from "../../components/BrandDashboardLayout";
import { Lock, ArrowRight, TrendingUp, MessageCircle, CheckCircle2, XCircle, Clock, Trophy } from "lucide-react";
import { isBrandPro } from "../../lib/plan";

const WINDOWS = [
  { v: 30, label: "30d" },
  { v: 90, label: "90d" },
  { v: 180, label: "180d" },
  { v: 365, label: "12 mo" },
];

export default function Analytics() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [windowDays, setWindowDays] = useState(90);
  const [error, setError] = useState(null);
  const isPro = isBrandPro(user);

  useEffect(() => {
    if (!isPro) { setLoading(false); return; }
    setLoading(true); setError(null);
    api.get(`/brand/analytics?window_days=${windowDays}`)
      .then(({ data }) => setData(data))
      .catch((e) => setError(e.response?.data?.detail || "Failed to load analytics"))
      .finally(() => setLoading(false));
  }, [windowDays, isPro]);

  if (!isPro) {
    return (
      <BrandDashboardLayout>
        <div className="p-6 md:p-12 max-w-3xl">
          <div className="label-eyebrow">Brand Pro</div>
          <h1 className="font-heading text-4xl md:text-5xl uppercase tracking-tighter mt-2" data-testid="analytics-title">Sponsorship Analytics</h1>
          <div className="surface p-8 mt-8 border-l-2 border-volt" data-testid="analytics-paywall">
            <Lock className="w-6 h-6 text-volt mb-4" />
            <div className="font-heading text-2xl uppercase">Analytics is Brand Pro</div>
            <p className="text-ink-muted text-sm mt-3 max-w-md">
              See your reply rate, deal conversion %, average response time, and top-performing sport cohorts. Know which partnerships actually pay off.
            </p>
            <Link to="/app/pricing" className="btn-volt inline-flex items-center gap-2 mt-6 text-sm" data-testid="analytics-upgrade-btn">
              See Brand Pro <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </BrandDashboardLayout>
    );
  }

  return (
    <BrandDashboardLayout>
      <div className="p-6 md:p-12">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-10">
          <div>
            <div className="label-eyebrow">Workflow · Brand Pro</div>
            <h1 className="font-heading text-4xl md:text-5xl uppercase tracking-tighter mt-2" data-testid="analytics-title">Sponsorship Analytics</h1>
            <p className="text-ink-muted mt-2 text-sm max-w-xl">Reply rates, deal conversion and response time across all your partnership conversations.</p>
          </div>
          <div className="flex border border-line" data-testid="analytics-window-toggle">
            {WINDOWS.map(w => (
              <button key={w.v} onClick={() => setWindowDays(w.v)} data-testid={`analytics-window-${w.v}`}
                className={`px-3 py-2 text-[10px] uppercase tracking-widest border-r border-line last:border-r-0 ${windowDays === w.v ? "bg-volt text-black" : "text-ink-muted hover:text-white"}`}>
                {w.label}
              </button>
            ))}
          </div>
        </div>

        {loading && <div className="surface p-12 text-center text-ink-muted" data-testid="analytics-loading">Loading…</div>}
        {error && <div className="surface p-10 text-center text-[#FF3B30]" data-testid="analytics-error">{error}</div>}

        {!loading && !error && data && (
          <>
            {/* KPI cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="analytics-kpis">
              <Kpi icon={MessageCircle} label="Partnerships started" value={data.totals.partnerships_started} testid="kpi-started" />
              <Kpi icon={TrendingUp} label="Reply rate" value={`${data.rates.reply_rate_pct}%`} sub={`${data.totals.replied} replied`} testid="kpi-reply-rate" />
              <Kpi icon={CheckCircle2} label="Conversion" value={`${data.rates.conversion_rate_pct}%`} sub={`${data.totals.accepted} accepted`} testid="kpi-conversion" accent />
              <Kpi icon={Clock} label="Avg response" value={data.response.avg_response_hours != null ? `${data.response.avg_response_hours}h` : "—"} sub={`${data.response.samples} samples`} testid="kpi-response" />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
              <Kpi icon={Trophy} label="Deals won" value={data.wins.deals_won} testid="kpi-wins" accent />
              <Kpi label="Won pipeline value" value={`£${(data.wins.won_value_gbp || 0).toLocaleString()}`} testid="kpi-won-value" accent />
              <Kpi icon={CheckCircle2} label="Accept after reply" value={`${data.rates.accept_after_reply_pct}%`} testid="kpi-accept-after-reply" />
              <Kpi icon={XCircle} label="Declined" value={data.totals.declined} sub={`${data.totals.open} open`} testid="kpi-declined" />
            </div>

            {/* Daily volume sparkline */}
            <div className="surface p-6 mb-8" data-testid="analytics-volume">
              <div className="flex items-baseline justify-between mb-5">
                <div>
                  <div className="label-eyebrow">Daily conversation starts</div>
                  <div className="font-heading text-xl uppercase mt-1">Outreach volume</div>
                </div>
                <div className="text-xs text-ink-muted">{data.daily_starts.length} active days</div>
              </div>
              {data.daily_starts.length === 0 ? (
                <div className="text-ink-muted text-sm py-10 text-center">No partnership activity in the last {windowDays} days.</div>
              ) : (
                <Sparkline series={data.daily_starts} />
              )}
            </div>

            {/* Top cohorts */}
            <div className="surface" data-testid="analytics-cohorts">
              <div className="p-6 border-b border-line">
                <div className="label-eyebrow">Cohort performance</div>
                <div className="font-heading text-xl uppercase mt-1">Top sports by conversion</div>
                <p className="text-xs text-ink-muted mt-2">Where your partnerships actually convert. Use this to refine your matcher briefs.</p>
              </div>
              {data.top_cohorts.length === 0 ? (
                <div className="px-6 py-10 text-center text-ink-muted text-sm">No cohort data yet. Start a few partnerships to populate this.</div>
              ) : (
                <div className="divide-y divide-line">
                  {data.top_cohorts.map(c => (
                    <div key={c.sport} className="px-6 py-4 grid grid-cols-2 md:grid-cols-6 gap-3 items-center" data-testid={`cohort-${c.sport.toLowerCase().replace(/\s+/g, "-")}`}>
                      <div className="font-heading uppercase tracking-tight md:col-span-2 truncate">{c.sport}</div>
                      <Stat label="Started" v={c.total} />
                      <Stat label="Replied" v={c.replied} />
                      <Stat label="Reply %" v={`${c.reply_rate}%`} />
                      <Stat label="Conversion %" v={`${c.conversion_rate}%`} accent />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </BrandDashboardLayout>
  );
}

function Kpi({ icon: Icon, label, value, sub, accent, testid }) {
  return (
    <div className={`surface p-4 ${accent ? "border-l-2 border-volt" : ""}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="label-eyebrow">{label}</div>
        {Icon && <Icon className={`w-4 h-4 ${accent ? "text-volt" : "text-ink-muted"}`} />}
      </div>
      <div className={`font-mono mt-2 ${accent ? "text-volt" : "text-white"} text-3xl leading-none`}>{value}</div>
      {sub && <div className="text-[10px] uppercase tracking-widest text-ink-muted mt-2">{sub}</div>}
    </div>
  );
}

function Stat({ label, v, accent }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-widest text-ink-muted">{label}</div>
      <div className={`font-mono text-sm mt-1 ${accent ? "text-volt" : "text-white"}`}>{v}</div>
    </div>
  );
}

function Sparkline({ series }) {
  // Render a minimal SVG bar chart of daily starts.
  const max = Math.max(1, ...series.map(s => s.starts));
  const W = 100; const H = 32; const n = series.length;
  const barW = n > 0 ? (W / n) : 0;
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-32" data-testid="sparkline">
        {series.map((s, i) => {
          const h = Math.max(0.5, (s.starts / max) * (H - 2));
          return <rect key={s.date} x={i * barW + 0.4} y={H - h} width={Math.max(0.6, barW - 0.8)} height={h} fill="#D7FF00" />;
        })}
      </svg>
      <div className="flex justify-between text-[9px] text-ink-muted mt-2 font-mono">
        <span>{series[0].date}</span>
        <span>peak: {max}</span>
        <span>{series[series.length - 1].date}</span>
      </div>
    </div>
  );
}
